import pluralize from 'pluralize'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useKey } from 'react-use'

import { reportClientError } from '../../../../app/_components/chrome/scripts/DataDogRumScript'
import {
  useTrackPageView,
  useTrackSearchResults,
} from '../../../../app/_providers/TrackingProvider.client'
import { useTranslation } from '../../../../app/_providers/TranslationProvider.client'
import { visualFiltersSection, visualFiltersSectionV2 } from '../../../../brunswick/__fixtures__'
import { useCollectionFilterQueryParamsContext } from '../../../../brunswick/containers/CollectionFilterQueryParamsContext'
import { usePagesDirectoryUpdateQueryParam } from '../../../../lib/adapters/next-routing-service/useUpdateQueryParam'
import { useCollectionViewProductImpressions } from '../../../../lib/hooks/useCollectionViewProductImpressions'
import { disableScroll, enableScroll } from '../../../../lib/utils'
import { AnalyticsContext } from '../../../../types/figs'
import { ExposedMethods } from '../Search/SearchOverlayForm/useViewModel'

import { useSearchPageQuery } from './hooks/useSearchPageQuery'

interface Props {
  error: Error | undefined
  query: string
  statusCode: number | undefined
}

export function useViewModel({ error, query, statusCode }: Props) {
  const { trackPageView } = useTrackPageView()

  const { selectedFilters } = useCollectionFilterQueryParamsContext()
  const { trackSearchResults } = useTrackSearchResults()
  const selectedItemFilters = useMemo(() => {
    const selectedFilterKeys = Object.keys(selectedFilters)
    return selectedFilterKeys.map(key => ({
      key,
      values: selectedFilters[key]!,
    }))
  }, [selectedFilters])

  const {
    fetchMoreSearchResults,
    searchFilters,
    searchNumItems,
    searchPageInfo,
    searchVariantGroups,
    searchSuggestions,
    status,
    fetchingMore,
  } = useSearchPageQuery({ query, selectedFilters: selectedItemFilters })

  // Treat each distinct query + filter combination as a new search for the sake of the batch
  // number on `view_item_list` events. Normalized (keys + values sorted) so that applying the same
  // filters in a different order does not produce a different key and spuriously reset the counter.
  const impressionResetKey = useMemo(() => {
    const normalizedFilters = [...selectedItemFilters]
      .map(filter => ({ key: filter.key, values: [...filter.values].sort() }))
      .sort((a, b) => a.key.localeCompare(b.key))
    return JSON.stringify({ query, filters: normalizedFilters })
  }, [query, selectedItemFilters])

  // Only feed resolved results to impression tracking. During a query/filter change the underlying
  // query returns the previous result set (via `data ?? previousData`); without this gate, clearing
  // the dedupe set on reset would re-fire those stale products as batch 1.
  const skusViewed = useMemo(
    () =>
      status === 'resolved'
        ? searchVariantGroups.map(
            variantGroup => variantGroup.defaultShopifyProduct.defaultShopifyVariant.sku
          )
        : [],
    [searchVariantGroups, status]
  )
  useCollectionViewProductImpressions({
    viewHandle: 'search',
    skusViewed,
    trackBatchNumber: true,
    resetKey: impressionResetKey,
  })

  const hasMoreItems = searchPageInfo?.hasNextPage
  const loadMoreItems = useCallback(async () => {
    await fetchMoreSearchResults({
      variables: {
        after: searchPageInfo?.endCursor,
      },
    })
  }, [fetchMoreSearchResults, searchPageInfo?.endCursor])

  const {
    magnolia: {
      searchPage: { relatedSearchesTitle, resultNoun },
    },
  } = useTranslation()

  const pageTitle = query ? `${query.toUpperCase()}` : undefined

  const [autoCompleteOverlayVisible, setAutoCompleteOverlayVisible] = useState<boolean>(false)

  const searchOverlayFormRef = useRef<ExposedMethods | undefined>(undefined)

  const onAutoComplete = useCallback((hasResults: boolean) => {
    if (hasResults) setAutoCompleteOverlayVisible(true)
  }, [])

  const hideAutoCompleteOverlay = useCallback(() => {
    setAutoCompleteOverlayVisible(false)
    searchOverlayFormRef.current?.clearSearchResults()
  }, [])

  useEffect(() => {
    if (autoCompleteOverlayVisible) {
      disableScroll()
    } else {
      enableScroll()
    }
  }, [autoCompleteOverlayVisible])

  useKey(
    'Escape',
    () => {
      if (autoCompleteOverlayVisible) {
        hideAutoCompleteOverlay()
      }
    },
    undefined,
    [autoCompleteOverlayVisible, hideAutoCompleteOverlay]
  )

  const updateQueryParam = usePagesDirectoryUpdateQueryParam()
  const submitForm = useCallback(
    (searchText: string) => {
      hideAutoCompleteOverlay()
      updateQueryParam('q', searchText)
    },
    [hideAutoCompleteOverlay, updateQueryParam]
  )

  useEffect(() => {
    // TODO: Fix this auto-generated suppression.
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (error) {
      reportClientError({
        error,
        context: {
          scope: 'Search Page',
          label: 'useViewModel error from GIP',
        },
      })
    }
  }, [error])

  const analyticsContext: AnalyticsContext = useMemo(
    () => ({
      category: 'Search Page',
      list: query,
    }),
    [query]
  )

  const numberOfResultsText: string | undefined =
    searchNumItems !== 0 ? `${searchNumItems} ${pluralize(resultNoun, searchNumItems)}` : undefined

  const substituteSearchTerm = useCallback(
    (nostoTitle: string | undefined) => nostoTitle?.replace('$searchTerm', query),
    [query]
  )

  const searchQueryTracker = useRef('')
  useEffect(() => {
    // TODO: Fix this auto-generated suppression.
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (status === 'resolved' && searchQueryTracker.current !== query) {
      trackSearchResults({
        id: `page_`,
        query: query,
        searchType: 'page',
        topResults: searchVariantGroups.map(variantGroup => {
          return {
            handle: variantGroup.product.handle,
            color: variantGroup.color?.id ?? '',
            id: variantGroup.id,
            soldOut: variantGroup.availability === 'OUT_OF_STOCK',
            shopifyId: variantGroup.defaultShopifyProduct.id,
          }
        }),
        totalResults: searchVariantGroups.length,
      })
      searchQueryTracker.current = query
    }
  }, [trackSearchResults, searchVariantGroups, query, status])

  useEffect(() => {
    trackPageView({
      pageViewName: 'Search',
      pageViewHandle: 'search',
      pageViewCategory: 'search',
      searchQuery: query,
    })
  }, [query, trackPageView])

  return {
    analyticsContext,
    autoCompleteOverlayVisible,
    filters: searchFilters,
    hasMoreItems,
    hideAutoCompleteOverlay,
    initialSearchText: query,
    status,
    fetchingMore,
    loadMoreItems,
    totalItemsCount: searchNumItems,
    numberOfResultsText,
    onAutoComplete,
    pageTitle,
    searchVariantGroups,
    relatedSearches: searchSuggestions,
    relatedSearchesTitle,
    searchOverlayFormRef,
    statusCode,
    submitForm,
    substituteSearchTerm,
    visualFiltersSection,
    visualFiltersSectionV2,
  }
}
